import { useEffect, useState, useRef, useMemo } from 'react';
import useServerTable from '../utils/useServerTable';
import useColumnPreferences, { useHiddenFilterReset } from '../utils/useColumnPreferences';
import * as XLSX from 'xlsx';
import Drawer from './Drawer';
import OrderForm from './OrderForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import ColumnPicker from './ui/ColumnPicker';
import { ColumnCells, ColumnFilterRow, ColumnHeaderRow, StatusBadge } from './ui/TableBits';
import { formatDate } from '../utils/dateUtils';
import { useToast } from './ui/Toast';

// One key per filterable column (issue #144); each maps to a same-named
// query param on GET /order (backend #131).
const EMPTY_FILTER = { employeeName: '', clientName: '', invoiceFrequency: '', status: '' };

// Matches OrderForm's frequency select.
const FREQUENCY_OPTIONS = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'BIWEEKLY', label: 'Biweekly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

function OrderTable() {
  const toast = useToast();
  const {
    rows: orders, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload, fetchAll,
  } = useServerTable('/order', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'employeeName', direction: 'asc' } });
  const [statusOptions, setStatusOptions] = useState([]);

  // Accumulate the status values seen across fetches to populate the Status
  // filter dropdown (the hook owns the fetch, so derive this from its rows).
  useEffect(() => {
    setStatusOptions(prev => {
      const set = new Set(prev);
      orders.forEach(b => { if (b?.status) set.add(b.status); });
      return Array.from(set).sort();
    });
  }, [orders]);

  // Drawer state
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  const openEdit = (b) => setDrawer({ mode: 'edit', id: b.id });
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); reload(); };

  const handleDownloadExcel = async () => {
    try {
      const rows = await fetchAll();
      const ws = XLSX.utils.json_to_sheet(rows.map(b => ({
        'Billing #': b.corpBillingNo, Employee: b.employeeName, Client: b.clientName,
        'Billing Code': b.billingCode, Rate: b.billRate, Frequency: b.invoiceFrequency,
        'Due Days': b.invoiceDueDays, Description: b.itemDescription,
        'Start Date': b.startDate, 'First Period End': b.firstPeriodEndDate,
        'End Date': b.endDate, Status: b.status,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Orders');
      XLSX.writeFile(wb, 'orders.xlsx');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  // Column catalogue in default order (issue #148). Employee is locked because
  // it is the row's open link; Client is the other half of the contract.
  const columnCatalogue = useMemo(() => [
    {
      key: 'employeeName', label: 'Employee', locked: true,
      filter: { name: 'employeeName' },
      cell: b => (
        <td style={{ fontWeight: '500' }}>
          {/* the employee name opens the order; internal id in the tooltip */}
          <button type="button" className="btn-link table-link" onClick={() => openEdit(b)} title={`Order ${b.corpBillingNo ?? ''} (internal id: ${b.id})`}>
            {b.employeeName}
          </button>
        </td>
      ),
    },
    {
      key: 'clientName', label: 'Client', locked: true,
      filter: { name: 'clientName' },
      cell: b => <td style={{ color: 'var(--muted)' }}>{b.clientName}</td>,
    },
    {
      key: 'billRate', label: 'Rate',
      cell: b => <td style={{ fontWeight: '600', color: 'var(--ink)' }}>{b.billRate}</td>,
    },
    {
      key: 'invoiceFrequency', label: 'Frequency',
      filter: { name: 'invoiceFrequency', options: FREQUENCY_OPTIONS },
      cell: b => <td>{b.invoiceFrequency}</td>,
    },
    {
      key: 'startDate', label: 'Start',
      cell: b => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(b.startDate)}</td>,
    },
    {
      key: 'endDate', label: 'End',
      cell: b => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(b.endDate)}</td>,
    },
    {
      key: 'firstPeriodEndDate', label: 'First Period End',
      cell: b => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(b.firstPeriodEndDate)}</td>,
    },
    {
      key: 'status', label: 'Status',
      filter: { name: 'status', options: statusOptions },
      cell: b => <td><StatusBadge status={b.status} /></td>,
    },
    // statusOptions arrives from the admin config fetch
  ], [statusOptions]);

  const {
    columns, pickerColumns, toggle, move, step, reset, isDefault, saveError,
  } = useColumnPreferences('billings', columnCatalogue);

  useEffect(() => { if (saveError) toast.error(saveError); }, [saveError, toast]);
  useHiddenFilterReset(columns, setFilter, EMPTY_FILTER);

  return (
    <div className="page-container">
      <PageHeader
        title="Orders"
        subtitle="Manage order rates and schedules"
        actions={
          <>
            <button className="btn-info" onClick={handleDownloadExcel}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
            <button className="btn-primary" onClick={openCreate}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Order
            </button>
          </>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
        <ColumnPicker
          columns={pickerColumns}
          onToggle={toggle}
          onMove={move}
          onStep={step}
          onReset={reset}
          isDefault={isDefault}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <ColumnHeaderRow columns={columns} sortConfig={sortConfig} onSort={handleSort} />
            <ColumnFilterRow columns={columns} filter={filter} onChange={handleFilterChange} />
          </thead>
          <tbody style={{ opacity: loading && orders.length > 0 ? 0.55 : 1 }}>
            {orders.length === 0 ? (
              <TableState
                colSpan={columns.length}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No order records yet"
                emptyHint="Create an order record to set rates and invoice schedules."
                emptyAction={{ label: 'New Order', onClick: openCreate }}
              />
            ) : orders.map(b => (
              <tr key={b.id}>
                <ColumnCells columns={columns} row={b} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit order' : 'New order'}
        subtitle={drawer?.mode === 'edit' ? 'Update rate, cycle, and dates.' : 'Create a new order record.'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              {drawer?.mode === 'edit' ? 'Save changes' : 'Create order'}
            </button>
          </>
        }
      >
        {drawer && (
          <OrderForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            orderId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default OrderTable;
